import { ProcessedParcel, WeekProgress, WeekBucket, RemittanceFile } from './types';
import { CustomerStage } from '../../customer';

const DEFAULT_STAGES: CustomerStage[] = ['ups', 'gls', 'invoice', 'remits'];

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Parses an MMDDYYYY week string (e.g. "05112026") into a Date.
 * Returns null when the input is malformed.
 */
export const parseWeekString = (week?: string): Date | null => {
  if (!week || week.length !== 8) return null;
  const month = parseInt(week.slice(0, 2), 10);
  const day = parseInt(week.slice(2, 4), 10);
  const year = parseInt(week.slice(4, 8), 10);
  if (!month || !day || !year) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

export const formatWeekTitle = (week?: string): string => {
  const d = parseWeekString(week);
  if (!d) return week ?? '—';
  return `Week of ${MONTH_FULL[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const formatRelativeWeek = (week?: string, now: Date = new Date()): string => {
  const d = parseWeekString(week);
  if (!d) return '';
  const diffDays = Math.floor((now.getTime() - d.getTime()) / MS_PER_DAY);
  if (diffDays < 0) return 'Upcoming';
  if (diffDays < 7) return 'Current week';
  if (diffDays < 14) return '1 week ago';
  const weeks = Math.floor(diffDays / 7);
  if (weeks < 5) return `${weeks} weeks ago`;
  const months = Math.floor(diffDays / 30);
  return months <= 1 ? '1 month ago' : `${months} months ago`;
};

export const computeProgress = (
  row: ProcessedParcel,
  stages: CustomerStage[] = DEFAULT_STAGES
): WeekProgress => {
  const aps = row.AccountsPayable ?? [];
  // can now have multiple remit files
  const remitsTotal = aps.reduce((n, ap) => n + Math.max(1, ap.RemittanceFiles?.length ?? 0), 0);
  const remitsUploaded = aps.reduce(
    (n, ap) =>
      n +
      (ap.RemittanceFiles?.length
        ? ap.RemittanceFiles.filter((f) => f.Uploaded === true).length
        : ap.RemittanceFileUploaded === true
        ? 1
        : 0),
    0
  );

  let total = 0;
  let done = 0;

  for (const stage of stages) {
    switch (stage) {
      case 'ups':
        total += 1;
        if (row.UPSImportUploaded) done += 1;
        break;
      case 'gls':
        total += 1;
        if (row.GLSImportUploaded) done += 1;
        break;
      case 'fbo':
        total += 1;
        if (row.FBOImportUploaded) done += 1;
        break;
      case 'dhl':
        total += 1;
        if (row.DHLImportUploaded) done += 1;
        break;
      case 'invoice':
        total += 1;
        if (row.InvoiceImportUploaded) done += 1;
        break;
      case 'remits':
        total += 1 + remitsTotal;
        if (remitsTotal > 0) done += 1;
        done += remitsUploaded;
        break;
    }
  }

  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return {
    done,
    total,
    percent,
    isComplete: total > 0 && done === total,
    remitsUploaded,
    remitsTotal,
  };
};

export const remitFilesOf = (ap: {
  RemittanceFileURL?: string | null;
  RemittanceFileUploaded?: boolean;
  RemittanceFiles?: RemittanceFile[];
}): RemittanceFile[] => {
  if (ap.RemittanceFiles && ap.RemittanceFiles.length > 0) return ap.RemittanceFiles;
  if (ap.RemittanceFileURL) {
    return [{ Part: 1, Url: ap.RemittanceFileURL, Uploaded: ap.RemittanceFileUploaded === true }];
  }
  return [];
};

export const hasPartialRemit = (ap: { AccountsPayableRemitFile: string | null }): boolean => {
  return Boolean(ap.AccountsPayableRemitFile);
};

export const buildSearchHaystack = (week?: string): string => {
  const d = parseWeekString(week);
  if (!d) return (week || '').toLowerCase();
  const m = d.getMonth();
  const day = d.getDate();
  const year = d.getFullYear();
  const month1 = m + 1;
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    week,
    MONTH_FULL[m],
    MONTH_LABELS[m],
    `${MONTH_FULL[m]} ${day}`,
    `${MONTH_LABELS[m]} ${day}`,
    `${MONTH_FULL[m]} ${day}, ${year}`,
    `${MONTH_LABELS[m]} ${day} ${year}`,
    `${month1}/${day}`,
    `${month1}/${day}/${year}`,
    `${month1}-${day}-${year}`,
    `${year}-${pad(month1)}-${pad(day)}`,
    String(year),
  ].join(' ').toLowerCase();
};

export const weekBucket = (week?: string, now: Date = new Date()): WeekBucket => {
  const d = parseWeekString(week);
  if (!d) return 'earlier';
  const diff = Math.floor((now.getTime() - d.getTime()) / MS_PER_DAY);
  if (diff < 0) return 'upcoming';
  if (diff < 30) return 'last30';
  if (diff < 90) return 'lastQuarter';
  return 'earlier';
};

export const WEEK_BUCKET_LABELS: Record<WeekBucket, string> = {
  upcoming: 'Upcoming',
  last30: 'Last 30 Days',
  lastQuarter: 'Last Quarter',
  earlier: 'Earlier',
};

export const isCurrentWeek = (week?: string, now: Date = new Date()): boolean => {
  const d = parseWeekString(week);
  if (!d) return false;
  const diff = Math.floor((now.getTime() - d.getTime()) / MS_PER_DAY);
  return diff >= 0 && diff < 7;
};

export const formatRelativeTime = (timestamp: number | null, now: number = Date.now()): string => {
  if (!timestamp) return '';
  const diff = Math.max(0, now - timestamp);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};
